import { DotsVertical, Plus } from '@untitledui/icons';
import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';
import { Button as AriaButton, Link as AriaLink } from 'react-aria-components';

import { useBreakpoint } from '@/hooks/useBreakpoint';
import { Dropdown } from '@/lib/base/overlays/dropdown';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';
import { cx } from '@/utils/cx';
import { isExternalHref } from '@/utils/isExternalHref';

import { Toolbar } from './toolbar';

export interface HeaderAction {
  /** Unique key for the action */
  id: string;
  /** Display label (used as visible text or aria-label when iconOnly) */
  label: string;
  /** Click handler (mutually exclusive with href) */
  onClick?: () => void;
  /** Link URL (mutually exclusive with onClick) */
  href?: string;
  /** Icon component rendered before the label (desktop only) */
  icon?: FC<{ className?: string }>;
  /** When true, only the icon is rendered and label is used as aria-label */
  iconOnly?: boolean;
  /**
   * When true the action is rendered but non-interactive (no click). Pair with
   * `disabledTooltip` to explain why — e.g. a "Add contact" CTA the user lacks
   * create permission for. Prefer this over omitting the action: a contextually
   * expected button that silently disappears is more confusing than a disabled
   * one with a reason.
   */
  isDisabled?: boolean;
  /** Tooltip content shown on hover/focus when `isDisabled` is true. */
  disabledTooltip?: ReactNode;
  /**
   * `primary` — always visible, styled as the main CTA (brand color).
   * `secondary` — bordered button with white background.
   * `unstyled` — text-only with no border or background.
   */
  variant?: 'primary' | 'secondary' | 'unstyled';
}

export interface PageLayoutProps {
  title: string;
  icon?: ReactNode;
  /**
   * Subtitle rendered inline next to the title. Accepts a string or
   * arbitrary JSX (e.g. for embedded links). When passing JSX, keep the
   * content compact and inline-friendly — it renders inside a `<span>`.
   */
  subtitle?: ReactNode;
  /**
   * Content rendered inline after the title and subtitle (e.g. a saved-view
   * tab strip). Sits at the end of the same flex-wrap row, so it follows the
   * subtitle/count and wraps below on narrow viewports.
   */
  titleTrailing?: ReactNode;
  actions?: HeaderAction[];
  /** When true (default), secondary actions collapse into overflow menu on mobile */
  collapseActions?: boolean;
  /**
   * Min-width breakpoint at/above which actions render fully inline; below it
   * (when `collapseActions`), secondary actions collapse into the overflow
   * menu. Default `'sm'` (640px). Raise it for content-heavy headers (many
   * actions + titleTrailing) that would otherwise crowd on medium widths.
   */
  collapseActionsBreakpoint?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  toolbar?: ReactNode;
  /** Show a bottom border on the toolbar (default: true). Set to false when
   *  content below provides its own top border (e.g., tables). */
  toolbarBorderBottom?: boolean;
  /** Additional className forwarded to the toolbar container. Use this to pin
   *  a min-height when the toolbar's content varies in intrinsic height
   *  across states (e.g. a selection-mode toolbar) — see Toolbar's className. */
  toolbarClassName?: string;
  children: ReactNode;
  className?: string;
  /** Optional className applied to the content area (below header/toolbar) */
  contentClassName?: string;
  /**
   * Embedded mode: drops the title row (title / subtitle / actions) so the
   * layout sits flush inside a host container (e.g. a record-page tab) and
   * tightens the top spacing above the toolbar. Horizontal padding (px-4) is
   * preserved in both modes so the toolbar/content line up with a standalone
   * page. Default false.
   */
  embedded?: boolean;
}

const PRIMARY_CLASS_BASE =
  'inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-bg-brand-solid)] text-sm font-semibold text-white shadow-xs-skeumorphic hover:bg-[var(--color-bg-brand-solid_hover)] transition-colors';
const UNSTYLED_CLASS =
  'inline-flex items-center gap-1.5 text-sm font-semibold text-fg-primary hover:text-fg-secondary transition-colors';
const SECONDARY_CLASS_BASE =
  'inline-flex items-center gap-1.5 rounded-lg bg-primary text-sm font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover transition-colors';
const MOBILE_PRIMARY_CLASS =
  'p-1.5 rounded bg-[var(--color-bg-brand-solid)] text-white';

// External URLs (and mailto:/tel:) must render as plain anchors — see
// isExternalHref() for why. Internal hrefs use `AriaLink` so navigation
// stays client-side (via AriaRouterProvider in Root.tsx) instead of
// reloading the whole app. Same split as lib/base/buttons/button.tsx,
// via the shared predicate.
function HeaderActionLink({
  href,
  className,
  ariaLabel,
  children,
}: {
  href: string;
  className: string;
  ariaLabel: string | undefined;
  children: ReactNode;
}) {
  if (isExternalHref(href)) {
    return (
      <a href={href} className={className} aria-label={ariaLabel}>
        {children}
      </a>
    );
  }
  return (
    <AriaLink href={href} className={className} aria-label={ariaLabel}>
      {children}
    </AriaLink>
  );
}

// Per-variant hover overrides that cancel a variant's hover affordance while
// disabled, so a disabled action doesn't appear interactive on hover.
const DISABLED_HOVER_NEUTRALIZE: Record<
  NonNullable<HeaderAction['variant']>,
  string
> = {
  primary: 'hover:bg-[var(--color-bg-brand-solid)]',
  secondary: 'hover:bg-primary hover:text-secondary',
  unstyled: 'hover:text-fg-primary',
};

function DesktopAction({ action }: { action: HeaderAction }) {
  const variant = action.variant ?? 'secondary';
  const showLabel = !action.iconOnly;

  const className =
    variant === 'unstyled'
      ? UNSTYLED_CLASS
      : cx(
          variant === 'primary' ? PRIMARY_CLASS_BASE : SECONDARY_CLASS_BASE,
          action.iconOnly ? 'p-2' : 'px-4 py-2.5'
        );

  const content = (
    <>
      {action.icon && <action.icon className='size-4' />}
      {showLabel && action.label}
    </>
  );

  // Disabled: render the content inside a focusable TooltipTrigger (itself an
  // AriaButton) rather than an interactive <a>/<button>, mirroring lib/base
  // Button's disabled pattern. The trigger stays focusable so the tooltip
  // fires on hover/focus, but carries disabled styling and no href/onClick.
  // This applies to EVERY variant — primary, secondary, and unstyled — so a
  // disabled secondary/unstyled action can no longer fire its handler.
  if (action.isDisabled) {
    const disabledTrigger = (
      <TooltipTrigger
        aria-label={action.label}
        aria-disabled='true'
        className={cx(
          className,
          'cursor-not-allowed opacity-60',
          DISABLED_HOVER_NEUTRALIZE[variant]
        )}
      >
        {content}
      </TooltipTrigger>
    );
    // Only mount a tooltip when there's content to show. Wrapping in a Tooltip
    // with an undefined title renders an empty hover/focus tooltip.
    if (action.disabledTooltip == null) {
      return disabledTrigger;
    }
    return (
      <Tooltip
        title={action.disabledTooltip}
        placement='bottom'
        interactive={typeof action.disabledTooltip !== 'string'}
      >
        {disabledTrigger}
      </Tooltip>
    );
  }

  if (action.href) {
    return (
      <HeaderActionLink
        href={action.href}
        className={className}
        ariaLabel={action.iconOnly ? action.label : undefined}
      >
        {content}
      </HeaderActionLink>
    );
  }

  // Primary keeps react-aria's AriaButton (onPress); secondary/unstyled use a
  // native button (onClick), preserving each variant's existing behavior.
  if (variant === 'primary') {
    return (
      <AriaButton
        onPress={action.onClick}
        className={className}
        aria-label={action.iconOnly ? action.label : undefined}
      >
        {content}
      </AriaButton>
    );
  }

  return (
    <button
      type='button'
      onClick={action.onClick}
      className={className}
      aria-label={action.iconOnly ? action.label : undefined}
    >
      {content}
    </button>
  );
}

function MobilePrimaryAction({ action }: { action: HeaderAction }) {
  const actionIcon = action.icon ? (
    <action.icon className='size-4' />
  ) : (
    <Plus className='size-4' />
  );
  if (action.isDisabled) {
    const disabledTrigger = (
      <TooltipTrigger
        aria-label={action.label}
        aria-disabled='true'
        className={cx(MOBILE_PRIMARY_CLASS, 'cursor-not-allowed opacity-60')}
      >
        {actionIcon}
      </TooltipTrigger>
    );
    // Only mount a tooltip when there's content to show. Wrapping in a Tooltip
    // with an undefined title renders an empty hover/focus tooltip.
    if (action.disabledTooltip == null) {
      return disabledTrigger;
    }
    return (
      <Tooltip
        title={action.disabledTooltip}
        placement='bottom'
        interactive={typeof action.disabledTooltip !== 'string'}
      >
        {disabledTrigger}
      </Tooltip>
    );
  }
  if (action.href) {
    return (
      <HeaderActionLink
        href={action.href}
        ariaLabel={action.label}
        className={MOBILE_PRIMARY_CLASS}
      >
        {actionIcon}
      </HeaderActionLink>
    );
  }
  return (
    <AriaButton
      onPress={action.onClick}
      aria-label={action.label}
      className={MOBILE_PRIMARY_CLASS}
    >
      {actionIcon}
    </AriaButton>
  );
}

export function PageLayout({
  title,
  icon,
  subtitle,
  titleTrailing,
  actions = [],
  collapseActions = true,
  collapseActionsBreakpoint = 'sm',
  toolbar,
  toolbarBorderBottom = true,
  toolbarClassName,
  children,
  className,
  contentClassName,
  embedded = false,
}: PageLayoutProps) {
  const isDesktop = useBreakpoint(collapseActionsBreakpoint);

  const primaryActions = useMemo(
    () => actions?.filter(a => a.variant === 'primary') ?? [],
    [actions]
  );
  const secondaryActions = useMemo(
    () => actions?.filter(a => a.variant !== 'primary') ?? [],
    [actions]
  );

  return (
    <div
      className={cx(
        'bg-bg-primary flex flex-col flex-1 min-h-0 overflow-hidden',
        className
      )}
    >
      {/* Header section. Horizontal padding (px-4) matches a standalone table
          page in both modes. The top padding gives a standalone page's TITLE
          row breathing room (pt-3 = 12px). Embedded mode drops the title row
          and the host (e.g. a record's tab bar) already provides ~10px below
          itself, so embedded adds pt-1.5 (6px) — the two together total ~16px,
          aligning the toolbar/table top border with a standalone page. (Use a
          standard spacing class, not an arbitrary `pt-[Npx]`, so the JIT
          reliably generates it.) */}
      <div className={cx('shrink-0 px-4', embedded ? 'pt-1.5' : 'pt-3')}>
        <div className='flex flex-col gap-3'>
          {/* Row 1: Title + icon | Actions */}
          {!embedded && (
            <div className='flex items-center justify-between gap-2'>
              {/*
              Icon + title stay locked together as a single inline unit.
              The subtitle is a separate flex sibling so it can wrap to a
              new line on narrow viewports (mobile) when it doesn't fit
              next to the title — short subtitles continue to render
              inline because they fit on one row.

              `flex-1`: the row takes all the width the actions leave, rather
              than sizing to its content. A `titleTrailing` slot that measures
              itself (the saved-view strip collapses pills into "More" when
              they don't fit) needs a width that doesn't depend on what it
              currently renders — content-sizing would shrink the row every
              time a pill collapsed and cascade to nothing. Visually
              identical: the actions were already pushed right by
              `justify-between`.
            */}
              <div className='flex flex-1 flex-wrap items-center gap-x-2 gap-y-0.5 min-w-0'>
                <div className='flex items-center gap-2'>
                  {icon}
                  <h1 className='text-lg font-semibold text-fg-primary'>
                    {title}
                  </h1>
                </div>
                {subtitle && (
                  /*
                  `empty:hidden` removes the span from the flex layout when
                  its child renders to nothing (e.g. a subtitle component
                  returning `null`), so callers can pass a component that
                  conditionally renders without leaving a stray flex gap.
                */
                  <span className='text-sm text-fg-tertiary empty:hidden'>
                    {subtitle}
                  </span>
                )}
                {titleTrailing}
              </div>

              {/* Actions */}
              {!collapseActions || isDesktop ? (
                <div className='flex shrink-0 items-center gap-3'>
                  {secondaryActions.map(action => (
                    <DesktopAction key={action.id} action={action} />
                  ))}
                  {primaryActions.map(action => (
                    <DesktopAction key={action.id} action={action} />
                  ))}
                </div>
              ) : (
                <div className='flex shrink-0 items-center gap-1'>
                  {primaryActions.map(action => (
                    <MobilePrimaryAction key={action.id} action={action} />
                  ))}
                  {secondaryActions.length > 0 && (
                    <Dropdown.Root>
                      <AriaButton
                        className='p-1.5 rounded hover:bg-[var(--color-bg-tertiary)]'
                        aria-label='More actions'
                      >
                        <DotsVertical className='size-5' />
                      </AriaButton>
                      <Dropdown.Popover placement='bottom end'>
                        <Dropdown.Menu>
                          {secondaryActions.map(action => (
                            <Dropdown.Item
                              key={action.id}
                              href={action.isDisabled ? undefined : action.href}
                              onAction={
                                action.isDisabled ? undefined : action.onClick
                              }
                              isDisabled={action.isDisabled}
                              disabledTooltip={
                                typeof action.disabledTooltip === 'string'
                                  ? action.disabledTooltip
                                  : undefined
                              }
                            >
                              {action.icon && (
                                <action.icon className='size-4' />
                              )}
                              {action.label}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown.Popover>
                    </Dropdown.Root>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Row 2: Toolbar (conditional) */}
          {toolbar && (
            <Toolbar
              borderBottom={toolbarBorderBottom}
              className={toolbarClassName}
            >
              {toolbar}
            </Toolbar>
          )}
        </div>
      </div>

      {/* Content area — fills remaining space */}
      <div
        className={cx(
          'flex-1 min-h-0 flex flex-col overflow-hidden',
          contentClassName
        )}
      >
        {children}
      </div>
    </div>
  );
}
